import { company, companyLocation, printIdentifiers } from './reference-data.js';


export function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface PrintColumn {
  label: string;
  align?: 'left' | 'right';
  width?: string;
}

export interface PrintFact {
  label: string;
  value: string;
}

export interface PrintTotal {
  label: string;
  value: string;
  strong?: boolean;
}

export interface PrintDocument {
  title: string;
  documentNo: string;
  facts: PrintFact[];
  columns: PrintColumn[];
  rows: string[][];
  totals?: PrintTotal[];
  narration?: string | null;
  notes?: string[];
  signatures?: [string, string];
  watermark?: string | null;
}

export const PAGE = `
  @page { size: A4; margin: 0; }
  html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { padding: 14mm 14mm 12mm; }
`;

const STYLE = `
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; position: relative; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #444; font-size: 11px; }
  h1 { font-size: 14px; margin: 14px 0 2px; text-transform: uppercase; letter-spacing: .08em; }
  .no { font-size: 12.5px; font-weight: 600; padding-bottom: 10px; }
  .facts { display: flex; flex-wrap: wrap; gap: 10px 28px; font-size: 11.5px; padding-bottom: 12px; }
  .facts b { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #999; padding: 5px 6px; vertical-align: top;
           text-align: left; font-size: 11.5px; }
  th { border-bottom: 1.2px solid #000; font-size: 10.5px; text-transform: uppercase;
       letter-spacing: .06em; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .totals { display: flex; justify-content: flex-end; padding-top: 10px; }
  .totals table { width: auto; min-width: 260px; }
  .totals td { border-bottom: 0; padding: 3px 6px; }
  .totals tr.strong td { border-top: 1.2px solid #000; font-weight: 600; padding-top: 5px; }
  .narration { padding-top: 12px; font-size: 11.5px; }
  .narration b { font-weight: 600; }
  .notes { padding-top: 12px; font-size: 11px; color: #333; }
  .sigs { display: flex; gap: 60px; padding-top: 56px; }
  .sig { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
  .foot { padding-top: 18px; font-size: 10px; color: #444; }
  .mark { position: fixed; top: 42%; left: 0; right: 0; text-align: center;
          font-size: 68px; font-weight: 700; letter-spacing: .12em; color: #000;
          opacity: .12; transform: rotate(-20deg); text-transform: uppercase; }
`;

export function letterhead(): string {
  const co = company();
  const location = companyLocation();
  const ids = printIdentifiers();
  return `
  <div class="head">
    <div style="display:flex; gap:10px; align-items:flex-start">
      ${co?.logoUrl ? `<img src="${esc(co.logoUrl)}" alt="" style="height:38px">` : ''}
      <div>
        <div class="co">${esc(co?.name ?? 'Company')}</div>
        ${co?.legalName && co.legalName !== co.name ? `<div class="muted">${esc(co.legalName)}</div>` : ''}
        ${location ? `<div class="muted">${esc(location)}</div>` : ''}
        ${co?.phone || co?.email ? `<div class="muted">${esc([co?.phone, co?.email].filter(Boolean).join(' · '))}</div>` : ''}
      </div>
    </div>
    <div class="muted" style="text-align:right">
      ${ids.map((i) => `<div>${esc(i.label)} ${esc(i.value)}</div>`).join('')}
    </div>
  </div>`;
}

function stamp(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

export function printDocument(doc: PrintDocument): void {
  const co = company();
  const align = (col: PrintColumn | undefined) => (col?.align === 'right' ? ' class="num"' : '');

  const head = doc.columns
    .map(
      (c) =>
        `<th${align(c)}${c.width ? ` style="width:${esc(c.width)}"` : ''}>${esc(c.label)}</th>`,
    )
    .join('');
  const body = doc.rows
    .map(
      (row) =>
        `<tr>${row.map((cell, i) => `<td${align(doc.columns[i])}>${esc(cell)}</td>`).join('')}</tr>`,
    )
    .join('');

  const totals = doc.totals?.length
    ? `<div class="totals"><table>${doc.totals
        .map(
          (t) =>
            `<tr${t.strong ? ' class="strong"' : ''}><td>${esc(t.label)}</td><td class="num">${esc(t.value)}</td></tr>`,
        )
        .join('')}</table></div>`
    : '';

  const [left, right] = doc.signatures ?? ['Prepared by', 'Received by'];

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(doc.documentNo || doc.title)}</title>
<style>${STYLE}</style></head>
<body>
  ${doc.watermark ? `<div class="mark">${esc(doc.watermark)}</div>` : ''}
  ${letterhead()}
  <h1>${esc(doc.title)}</h1>
  <div class="no">${esc(doc.documentNo)}</div>
  <div class="facts">${doc.facts
    .filter((f) => f.value)
    .map((f) => `<span><b>${esc(f.label)}</b> ${esc(f.value)}</span>`)
    .join('')}</div>

  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>
  ${totals}
  ${doc.narration ? `<div class="narration"><b>Narration</b> ${esc(doc.narration)}</div>` : ''}
  ${
    doc.notes?.length
      ? `<div class="notes">${doc.notes.map((n) => `<div>${esc(n)}</div>`).join('')}</div>`
      : ''
  }

  <div class="sigs">
    <div class="sig">&nbsp;<div class="muted">${esc(left)}</div></div>
    <div class="sig">&nbsp;<div class="muted">${esc(right)}</div></div>
  </div>

  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    Printed ${esc(stamp(new Date().toISOString()))}.
  </div>
</body></html>`;

  send(html);
}


export type ReportRow = string[] | { cells: string[]; head?: boolean; total?: boolean };

export interface ReportTable {
  title: string;
  meta?: string | null;
  columns: PrintColumn[];
  rows: ReportRow[];
  notes?: string[];
  fileName?: string;
}

function reportRow(row: ReportRow): { cells: string[]; head: boolean; total: boolean } {
  return Array.isArray(row)
    ? { cells: row, head: false, total: false }
    : { cells: row.cells, head: row.head ?? false, total: row.total ?? false };
}

const REPORT_STYLE = `
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #333; font-size: 11px; }
  h1 { font-size: 14px; margin: 14px 0 2px; text-transform: uppercase; letter-spacing: .08em; }
  .meta { font-size: 11.5px; color: #333; padding-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 4px 6px; text-align: left; font-size: 11.5px; vertical-align: top;
           border-bottom: 1px solid #d0d0d0; }
  th { border-bottom: 1.2px solid #000; border-top: 1.2px solid #000;
       font-size: 10px; text-transform: uppercase; letter-spacing: .07em; font-weight: 600; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tr.head td { font-weight: 600; border-bottom: 1px solid #888; }
  tr.total td { font-weight: 600; border-top: 1px solid #000; border-bottom: 0; }
  .notes { padding-top: 12px; font-size: 11px; color: #333; }
  .foot { padding-top: 14px; margin-top: 14px; border-top: 1px solid #d0d0d0;
          font-size: 10px; color: #333; }
`;

export function printReport(report: ReportTable): void {
  const co = company();
  const align = (col: PrintColumn | undefined) => (col?.align === 'right' ? ' class="num"' : '');

  const head = report.columns
    .map(
      (c) =>
        `<th${align(c)}${c.width ? ` style="width:${esc(c.width)}"` : ''}>${esc(c.label)}</th>`,
    )
    .join('');

  const body = report.rows
    .map((raw) => {
      const row = reportRow(raw);
      const cls = row.total ? ' class="total"' : row.head ? ' class="head"' : '';
      const cells = report.columns
        .map((_, i) => `<td${align(report.columns[i])}>${esc(row.cells[i] ?? '')}</td>`)
        .join('');
      return `<tr${cls}>${cells}</tr>`;
    })
    .join('');

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(report.fileName ?? report.title)}</title>
<style>${REPORT_STYLE}</style></head>
<body>
  ${letterhead()}
  <h1>${esc(report.title)}</h1>
  ${report.meta ? `<div class="meta">${esc(report.meta)}</div>` : '<div class="meta"></div>'}

  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>
  ${
    report.notes?.length
      ? `<div class="notes">${report.notes.map((n) => `<div>${esc(n)}</div>`).join('')}</div>`
      : ''
  }
  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    Printed ${esc(stamp(new Date().toISOString()))}.
  </div>
</body></html>`;

  send(html);
}

export function send(html: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);

  const win = frame.contentWindow;
  if (!win) {
    frame.remove();
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.addEventListener('afterprint', () => frame.remove(), { once: true });

  let sent = false;
  const print = () => {
    if (sent) return;
    sent = true;
    win.focus();
    win.print();
  };
  win.addEventListener('load', print);
  if (win.document.readyState === 'complete') print();
}
